<template>
  <modal :show.sync="show" :on-close="onClose" :close-on-backdrop-click="false">
    <div class="column content-box">
      <woot-modal-header
        :header-title="$t('INTEGRATION_SETTINGS.WEBHOOK.ADD.TITLE')"
        :header-content="
          useInstallationName(
            $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.DESC'),
            globalConfig.installationName
          )
        "
      />
      <form class="row" @submit.prevent="addWebhook">
        <div class="small-12 columns">
          <label :class="{ error: $v.endPoint.$error }">
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.FORM.END_POINT.LABEL') }}
            <input
              v-model.trim="endPoint"
              type="text"
              name="endPoint"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.WEBHOOK.ADD.FORM.END_POINT.PLACEHOLDER'
                )
              "
              @input="$v.endPoint.$touch"
            />
            <span v-if="$v.endPoint.$error" class="message">
              {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.FORM.END_POINT.ERROR') }}
            </span>
          </label>
        </div>

        <div class="small-12 columns">
          <label>
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.WEBHOOK_TYPE.LABEL') }}
          </label>
          <select v-model="webhookType" @change="onWebhookTypeChange">
            <option
              v-for="type in webhookTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
        </div>

        <div class="small-12 columns">
          <label
            :class="{
              disabled: !isInboxType,
              error: $v.inboxId.$error,
            }"
          >
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.INBOX_ID.LABEL') }}
            <select
              v-model="inboxId"
              :disabled="!isInboxType"
              @change="$v.inboxId.$touch"
            >
              <option value="">
                {{
                  $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.INBOX_ID.PLACEHOLDER')
                }}
              </option>
              <option
                v-for="inbox in filteredInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <span v-if="$v.inboxId.$error" class="message">
              {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.INBOX_ID.ERROR') }}
            </span>
          </label>
        </div>

        <div
          class="small-12 columns"
          :class="{ error: $v.selectedEvents.$error }"
        >
          <label>
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.EVENTS.LABEL') }}
          </label>
          <label v-for="event in availableEvents" :key="event.value">
            <input
              v-model="selectedEvents"
              type="checkbox"
              :value="event.value"
              @change="$v.selectedEvents.$touch"
            />
            {{ event.label }}
          </label>
          <span
            v-if="$v.selectedEvents.$error"
            class="message"
            style="margin-top: 0; display: block"
          >
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.EVENTS.ERROR') }}
          </span>
        </div>

        <div class="modal-footer">
          <div class="small-12 columns">
            <woot-button
              :disabled="isSubmitDisabled"
              :is-loading="addWebHook.showLoading"
            >
              {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.FORM.SUBMIT') }}
            </woot-button>
            <woot-button class="button clear" @click.prevent="onClose">
              {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.CANCEL') }}
            </woot-button>
          </div>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { required, url, minLength, requiredIf } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import Modal from '../../../../components/Modal';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import { mapGetters } from 'vuex';
import {
  INTEGRATION_EVENTS,
  WEBHOOK_TYPES,
  ACCOUNT_TYPE_EVENTS,
  INBOX_TYPE_EVENTS,
} from './constants';

export default {
  components: {
    Modal,
  },
  mixins: [alertMixin, globalConfigMixin],
  props: {
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      endPoint: '',
      addWebHook: {
        showAlert: false,
        showLoading: false,
      },
      show: true,
      webhookType: 'account_type',
      inboxId: '',
      webhookTypes: WEBHOOK_TYPES.map(type => ({
        value: type.value,
        label: this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.ADD.WEBHOOK_TYPE.${type.label}`
        ),
      })),
      integrationEvents: INTEGRATION_EVENTS.map(event => ({
        value: event.value,
        label: this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.ADD.EVENTS.${event.label}`
        ),
      })),
      selectedEvents: [],
    };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
    }),
    isInboxType() {
      return this.webhookType === 'inbox_type';
    },
    availableEvents() {
      return this.integrationEvents.filter(event => {
        if (this.isInboxType) {
          return INBOX_TYPE_EVENTS.includes(event.value);
        }
        return ACCOUNT_TYPE_EVENTS.includes(event.value);
      });
    },
    filteredInboxes() {
      return this.inboxes.filter(inbox => inbox.chat_supported);
    },
    isSubmitDisabled() {
      return this.addWebHook.showLoading;
    },
  },
  validations: {
    inboxId: {
      requiredIf: requiredIf(function isInboxTypeSelected() {
        return this.isInboxType;
      }),
    },
    selectedEvents: {
      required,
    },
    endPoint: {
      required,
      minLength: minLength(7),
      url,
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
  },
  methods: {
    resetForm() {
      this.endPoint = '';
      this.$v.endPoint.$reset();
    },
    async addWebhook() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.addWebHook.showLoading = true;

      try {
        await this.$store.dispatch('webhooks/create', {
          webhook: {
            url: this.endPoint,
            subscriptions: this.selectedEvents,
            webhook_type: this.webhookType,
            inbox_id: this.isInboxType ? this.inboxId : null,
          },
        });
        this.addWebHook.showLoading = false;

        this.addWebHook.message = this.$t(
          'INTEGRATION_SETTINGS.WEBHOOK.ADD.API.SUCCESS_MESSAGE'
        );
        this.resetForm();
        this.onClose();
      } catch (error) {
        this.addWebHook.showLoading = false;
        this.addWebHook.message =
          error.response.data.message ||
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.API.ERROR_MESSAGE');
      } finally {
        this.addWebHook.showLoading = false;
        this.showAlert(this.addWebHook.message);
      }
    },
    onWebhookTypeChange() {
      this.selectedEvents = [];
      this.inboxId = '';
    },
    handleCheckboxChange() {
      this.selectedEvents = this.selectedEvents.filter(event => {
        return (
          this.integrationEvents.find(e => e.value === event) &&
          this.$refs[event].checked
        );
      });
    },
  },
};
</script>
